<template>
  <div class="approval-pending">
    <!-- TKP-103 / md. 33–40 — onaylayan tarafı. TKP-156: bekleyen liste → seçilen paket. TKP-157 / tunesoft2 s.7: satır bazında karar
         (onayla anında envantere; düzenleme iste açıklamayla; onaylama gerekçeyle), "Geri Gönder" ve "Tamamlandı". -->
    <div v-if="pending.length === 0" class="text-muted" data-test="pending-empty">{{ $t("approval.pending-empty") }}</div>

    <!-- TKP-156 / tunesoft2 s.5: "Onay Bekleyen Taslaklar" listesi — taslak adı, gönderen, gönderme zamanı; görüntüleme ikonu paketi açar -->
    <div v-else>
      <h6>{{ $t("approval.pending-list-title") }}</h6>
      <table class="table table-sm" data-test="pending-list">
        <thead>
          <tr>
            <th>{{ $t("approval.history.name") }}</th>
            <th>{{ $t("approval.history.submitted-by") }}</th>
            <th>{{ $t("approval.history.submitted-at") }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="pkg in pending" :key="pkg._id" data-test="pending-row" :class="{ 'table-active': selectedId === pkg._id }">
            <td>{{ pkg.name || $t("approval.department") }}</td>
            <td>{{ pkg.authorName || "—" }}</td>
            <td><small>{{ pkg.submittedAt | date(true) }}</small></td>
            <td class="text-right"><a href="javascript:;" data-test="pending-view" v-b-tooltip.hover :title="$t('approval.history.view')" @click="selectedId = pkg._id"><i class="bx bx-search-alt"></i></a></td>
          </tr>
        </tbody>
      </table>
    </div>

    <b-card v-for="pkg in selectedPackages" :key="pkg._id" class="mb-3" data-test="pending-package">
      <template #header>
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <strong data-test="pending-name">{{ pkg.name || $t("approval.department") }}</strong>
            <span class="ml-2">{{ pkg.authorName || "—" }}</span>
            <small class="text-muted ml-2">{{ $t("approval.submitted-at") }}: {{ pkg.submittedAt | date }}</small>
            <b-badge variant="warning" class="ml-2">{{ pkg.edits.length }} {{ $t("approval.edits") }}</b-badge>
            <!-- TKP-134: kademe — birim onayı (ön onay) / Uyum onayı; Uyum kademesinde birim onaylayanının adı -->
            <b-badge variant="info" class="ml-2" :data-test="`pending-stage-${pkg.stage || 'LEGAL'}`">{{ $t(stageLabelKey(pkg.stage)) }}</b-badge>
            <small v-if="pkg.departmentApproverName" class="ml-2 text-muted" data-test="pre-approved-by">{{ $t("approval.pre-approved-by", { name: pkg.departmentApproverName }) }}</small>
          </div>
        </div>
      </template>

      <draft-rows-table :edits="pkg.edits" decidable readonly @decide="onDecide(pkg, $event)" />

      <!-- tunesoft2 s.7: Geri Gönder (düzenleme istenen satır varsa) · Tamamlandı (düzenleme istenen satır varken sistem izin vermez) -->
      <div class="d-flex justify-content-between align-items-center mt-2">
        <small class="text-muted">{{ $t("approval.complete-hint") }}</small>
        <div>
          <b-button size="sm" variant="primary" class="mr-2" :disabled="isBusy || !canReturn(pkg)" data-test="return" @click="returnPackage(pkg)">{{ $t("approval.return") }}</b-button>
          <b-button size="sm" variant="success" :disabled="isBusy || !canComplete(pkg)" data-test="complete" @click="complete(pkg)">{{ $t("approval.complete") }}</b-button>
        </div>
      </div>
    </b-card>

    <!-- düzenleme isteği / onaylamama açıklaması -->
    <b-modal v-model="reasonDialog.show" :title="reasonDialog.decision === 'REJECT' ? $t('approval.row.reason-reject') : $t('approval.row.reason-request')" :ok-title="$t('common.save')" :cancel-title="$t('common.cancel')" :ok-disabled="!reasonDialog.reason.trim()" data-test="reason-modal" @ok="confirmReason">
      <!-- TKP-237 o.1 / o.2: alan adı + bilgi işareti; gerekçe/açıklama zorunlu (Kaydet boşken pasif) -->
      <label class="font-weight-bold">
        {{ reasonDialog.decision === "REJECT" ? $t("approval.row.reason-reject") : $t("approval.row.reason-request") }}
        <i class="bx bx-info-circle text-info" data-test="reason-hint" v-b-tooltip.hover :title="reasonDialog.decision === 'REJECT' ? $t('approval.row.reason-reject-hint') : $t('approval.row.reason-request-hint')"></i>
      </label>
      <b-form-textarea v-model="reasonDialog.reason" rows="3" data-test="reason" />
    </b-modal>

    <!-- TKP-156 / tunesoft2 s.5: onayladığım taslakların tarihçesi (varsayılan kapalı, salt okunur) -->
    <approval-history scope="decided" />
  </div>
</template>

<script>
import ApprovalHistory from "./approval-history";
import DraftRowsTable from "./draft-rows-table";
import { canCompletePackage, canReturnPackage, stageLabelKey } from "./approval";

export default {
  name: "approval-pending",
  components: { ApprovalHistory, DraftRowsTable },
  data() {
    return { selectedId: null, reasonDialog: { show: false, packageId: null, editIds: [], decision: null, reason: "" } };
  },
  computed: {
    pending() {
      return this.$store.getters["approval/pending"];
    },
    isBusy() {
      return this.$store.getters["approval/isBusy"];
    },
    /** Listeden seçilen paket (varsayılan: ilk bekleyen) — detay ve kararlar yalnız onun için çizilir. */
    selectedPackages() {
      const chosen = this.pending.find((p) => p._id === this.selectedId) || this.pending[0];
      return chosen ? [chosen] : [];
    },
  },
  async mounted() {
    await this.load();
  },
  methods: {
    stageLabelKey,
    canReturn: canReturnPackage,
    canComplete: canCompletePackage,
    async load() {
      try {
        await this.$store.dispatch("approval/fetchPending");
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    /** Satır kararı: onay hemen gider; düzenleme isteği ve onaylamama önce açıklama ister. */
    /** TKP-237 t.8: toplu işlemin satırları birlikte — tek karar, tek gerekçe, tek bildirim. */
    async onDecide(pkg, { edit, edits, decision }) {
      const editIds = (edits && edits.length ? edits : [edit]).map((e) => e._id);
      if (decision === "APPROVE") return this.decide(pkg._id, editIds, decision);
      this.reasonDialog = { show: true, packageId: pkg._id, editIds, decision, reason: "" };
    },
    async confirmReason() {
      const { packageId, editIds, decision, reason } = this.reasonDialog;
      await this.decide(packageId, editIds, decision, reason.trim());
    },
    async decide(packageId, editIds, decision, reason) {
      try {
        let data = null;
        for (const editId of [].concat(editIds)) data = (await this.$store.dispatch("approval/decideRow", { packageId, editId, decision, reason })).data;
        if (decision === "APPROVE") {
          // TKP-241 p.5: aynı isimde varlık — sunucunun cümlesi olduğu gibi ("İlgili isimde bir varlık … kaydedilmedi.")
          if (data && data.failed) this.$toast.warning(data.failedCode === "DUPLICATE" ? data.failed : this.$t("approval.row.failed-toast", { reason: data.failed }));
          else if (data && data.forwarded) this.$toast.success(this.$t("approval.row.forwarded-toast"));
          else this.$toast.success(this.$t(data && data.applied ? "approval.row.applied-toast" : "approval.row.applied"));
        } else this.$toast.success(this.$t(decision === "REJECT" ? "approval.row.rejected-toast" : "approval.row.requested-toast"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async returnPackage(pkg) {
      try {
        await this.$store.dispatch("approval/returnPackage", pkg._id);
        this.$toast.success(this.$t("approval.returned-package"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async complete(pkg) {
      try {
        const { data } = await this.$store.dispatch("approval/complete", { packageId: pkg._id, decisions: [] });
        this.$toast.success(this.$t("approval.completed", { applied: data.applied, returned: (data.rejected || 0) + (data.failed || 0) }));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
  },
};
</script>
